import graphqlClient from "../utils/graphql_client_util";
import { SessionStorageUtil } from "../utils/session_storage_util";

const MUTATIONS = {
  SEND_SMS_VERIFICATION: `
    mutation SendVerification($input: SendSmsVerificationInput!) {
      sendVerification(input: $input) {
        success
        error {
          message
          errorType
          code
        }
        data {
          verificationCode
          smsVerificationHistorySeq
          regDate
          phoneNumber
          isVerified
          expiresAt
        }
      }
    }
  `,

  VERIFY_PHONE_CODE: `
    mutation ConfirmVerification($input: ConfirmVerificationInput!) {
      confirmVerification(input: $input) {
        success
        error {
          message
          errorType
          code
        }
        data {
          userSeq
          refreshToken
          accessToken
        }
      }
    }
  `,

  LOGIN: `
    mutation Login($input: LoginInput!) {
      login(input: $input) {
        success
        error {
          code
          errorType
          message
        }
        data {
          accessToken
          refreshToken
          userSeq
        }
      }
    }
  `,

  VERIFY_SOCIAL_LOGIN: `
    mutation VerifySocialLogin($input: SocialLoginInput!) {
      verifySocialLogin(input: $input) {
        success
        error {
          code
          errorType
          message
        }
        data {
          accessToken
          idToken
          membershipStatus
          message
          providerEmail
          providerId
          providerName
          refreshToken
          sessionCount
          userSeq
        }
      }
    }
  `,
};

const QUERIES = {
  USER_BY_SEQ: `
    query UserBySeq($userSeq: ID!) {
      userBySeq(userSeq: $userSeq) {
        success
        error {
          code
          errorType
          message
        }
        data {
          userEmail
          userPhone
          userParentPhone
          userPhoneVerified
          userGender
          userBirthdate
          userGroup
          subscription
          userTerms
          userPrivacy
          userMarketing
          voiceDiaryAgreeYn
          voiceDiaryAgreeDate
          restoreYn
          userIsDeleted
          userDeletedReason
          userDeletedDate
          userCreatedDate
          userLastLoginDate
          userGroupName
          userGenderName
          subscriptionName
          loginType
          salesCode
          salesManSeq
          salesManType
          salesManTypeName
          regName
          regDate
          modName
          modDate
          totalEarned
          totalUsed
          totalExpired
          totalRefunded
          balance
          receipt {
            userName
            receiptTypeName
            receiptTypeDesc
            osType
            osTypeName
            osTypeDesc
            subscriptionStorePeriod
            subscriptionOriginalStoreToken
            notificationType
            notificationSubType
            subscriptionRefundReason
            subscriptionRefundDate
            amount
            currency
            modDate
            modName
            receiptId
            receiptType
            regDate
            regName
            subscriptionExpiredDate
            subscriptionStartedDate
            subscriptionStoreId
            subscriptionStoreToken
            userSeq
          }
        }
      }
    }
  `,

  LOGOUT: `
    query Logout {
      logout {
        success
        error {
          code
          message
          errorType
        }
        data {
          message
          success
        }
      }
    }
  `,
};

/**
 * 토큰 및 userSeq를 SessionStorage에 저장하는 공통 헬퍼
 */
const saveTokens = ({ accessToken, refreshToken, userSeq }) => {
  SessionStorageUtil.setItem("accessToken", accessToken);
  SessionStorageUtil.setItem("refreshToken", refreshToken);
  SessionStorageUtil.setItem("userSeq", userSeq.toString());
};

/**
 * userBySeq로 유저 전체 정보를 조회하고 SessionStorage에 저장
 * 로그인 직후 accessToken + userSeq가 세션에 저장된 상태에서 호출
 * phoneNumber, userEmail 등 앱 전역에서 필요한 값을 모두 여기서 채움
 */
export const fetchAndStoreUser = async () => {
  try {
    const userSeq = SessionStorageUtil.getItem("userSeq");
    if (!userSeq) return null;

    const data = await graphqlClient.query(
      QUERIES.USER_BY_SEQ,
      { userSeq: userSeq.toString() },
      { useAuth: true }
    );

    if (data.userBySeq.success) {
      const user = data.userBySeq.data;

      // 앱 전역에서 공통으로 사용하는 값들을 SessionStorage에 저장
      SessionStorageUtil.setItem("phoneNumber", user.userPhone || "");
      SessionStorageUtil.setItem("userEmail", user.userEmail || "");
      SessionStorageUtil.setItem("userGroup", user.userGroup || "");
      SessionStorageUtil.setItem(
        "subscription",
        JSON.stringify(user.subscription ?? {})
      );
      SessionStorageUtil.setItem("userInfo", JSON.stringify(user));

      return user;
    } else {
      console.error("userBySeq error:", data.userBySeq.error);
      return null;
    }
  } catch (err) {
    console.error(err);
    return null;
  }
};

/**
 * SessionStorage 캐시에서 유저 정보를 반환
 * 캐시가 없으면 API를 다시 호출해 저장 후 반환
 */
export const getCurrentUser = async () => {
  try {
    const cached = SessionStorageUtil.getItem("userInfo");
    if (cached) return JSON.parse(cached);
    return await fetchAndStoreUser();
  } catch (err) {
    console.error(err);
    return null;
  }
};

export const requestPhoneAuth = async (phone_number) => {
  try {
    const data = await graphqlClient.mutation(
      MUTATIONS.SEND_SMS_VERIFICATION,
      { input: { phoneNumber: phone_number } },
      { useAuth: false }
    );

    if (data.sendVerification.success) {
      return { success: true, data: data.sendVerification.data };
    } else {
      return { success: false, error: data.sendVerification.error };
    }
  } catch (err) {
    console.error(err);
    return {};
  }
};

export const verifyPhoneCode = async (phone_number, verification_code) => {
  try {
    const data = await graphqlClient.mutation(
      MUTATIONS.VERIFY_PHONE_CODE,
      {
        input: {
          phoneNumber: phone_number,
          verificationCode: verification_code,
        },
      },
      { useAuth: false }
    );

    if (data.confirmVerification.success) {
      return { success: true, data: data.confirmVerification.data };
    } else {
      return { success: false, error: data.confirmVerification.error };
    }
  } catch (err) {
    console.error(err);
    return {};
  }
};

/**
 * 아이디/비밀번호 로그인
 * 성공 후 userBySeq 조회 → phoneNumber 등 전역 정보 SessionStorage에 저장
 */
export const login = async (userId, password) => {
  try {
    const data = await graphqlClient.mutation(
      MUTATIONS.LOGIN,
      { input: { userId, password } },
      { useAuth: false }
    );

    if (data.login.success) {
      saveTokens(data.login.data);

      // 로그인 직후 유저 정보 조회 (phoneNumber 포함)
      await fetchAndStoreUser();

      return { success: true, data: data.login.data };
    } else {
      return { success: false, error: data.login.error };
    }
  } catch (err) {
    console.error(err);
    return {};
  }
};

/**
 * 소셜 로그인 (Google / Apple)
 * provider: 'GOOGLE' | 'APPLE'
 * tokenId: idToken from social SDK
 * 성공 후 userBySeq 조회 → phoneNumber 등 전역 정보 SessionStorage에 저장
 */
/**
 * 로그아웃
 * logout mutation 실행 후 SessionStorage 전체 초기화
 */
export const logout = async () => {
  try {
    const data = await graphqlClient.mutation(
      QUERIES.LOGOUT,
      {},
      { useAuth: true }
    );

    if (data.logout.success) {
      SessionStorageUtil.clearAll();
      return { success: true, data: data.logout.data };
    } else {
      // 서버 오류가 있더라도 클라이언트 세션은 초기화
      SessionStorageUtil.clearAll();
      return { success: false, error: data.logout.error };
    }
  } catch (err) {
    console.error(err);
    SessionStorageUtil.clearAll();
    return {};
  }
};

export const verifySocialLogin = async (
  provider,
  tokenId,
  providerName = ""
) => {
  try {
    const variables = { provider, tokenId };
    if (providerName) variables.providerName = providerName;

    const data = await graphqlClient.mutation(
      MUTATIONS.VERIFY_SOCIAL_LOGIN,
      { input: variables },
      { useAuth: false }
    );

    if (data.verifySocialLogin.success) {
      const loginData = data.verifySocialLogin.data;

      if (loginData.accessToken) {
        saveTokens({
          accessToken: loginData.accessToken,
          refreshToken: loginData.refreshToken,
          userSeq: loginData.userSeq,
        });

        // 로그인 직후 유저 정보 조회 (phoneNumber 포함)
        await fetchAndStoreUser();
      }

      return { success: true, data: loginData };
    } else {
      return { success: false, error: data.verifySocialLogin.error };
    }
  } catch (err) {
    console.error(err);
    return {};
  }
};
